/**
 * @typedef {Object} WeeklySchedulesCardProps
 *
 * @property {Array<string>} services
 *  List of all services assigned to the logged-in user.
 *
 * @property {number} pendingScheduleCount
 *  Total number of schedules that require approval. Defaults to 0.
 *
 * @property {Array<Object>} schedules
 *  List of schedule objects for the current week. Defaults to empty array.
 *
 * @property {boolean} schedulesLoading
 *  Loading state for the schedule list.
 *
 * @property {(count: number, services: string[], key: string) => string | number} formatPendingCount
 *  Function to format or compute the pending schedule count.
 *
 * @property {() => void} handleCreateSchedule
 *  Callback triggered when "Create schedule" button is clicked.
 *
 * @property {() => void} handleViewSchedules
 *  Callback triggered when "View schedules" button is clicked.
 *
 * @property {() => void} handleRequireApprovalNavigation
 *  Callback triggered when user clicks the "Require approval" section.
 *
 * @property {Array<string>} createScheduleServices
 *  List of service keys required to enable the "Create schedule" button.
 *  Example: ["BCScheduledTxn", "BOScheduledTxn"]
 *
 * @property {string} viewScheduleService
 *  Single service key required to enable the "View schedules" button.
 *  Example: "BVScheduledTxn"
 *
 * @property {string} approvalService
 *  Service key used to determine if "Require approval" section is clickable.
 *  Example: "IAScheduledTxn"
 *
 * @property {Array<Object>} actions
 *  List of action button configs. Each item supports:
 *  - service: string | string[] — required service(s) to enable the button.
 *  - disableIfHasService?: string | string[] — OPT-IN. If the user has ALL of
 *    these services (in addition to satisfying service), the button is
 *    treated as a conflict: it renders disabled and clicking it shows an
 *    "unauthorized" toast instead of navigating. Omit this field entirely to
 *    keep the button's original missing-service-only behavior.
 *  - unauthorizedMessage?: string — custom toast text for the conflict case.
 */

/**
 * @param {WeeklySchedulesCardProps} props
 * @returns {JSX.Element}
 */
import ICONS from "../../../assets/Icons";
import { GlobalConfiguration } from "../../../utils/configurationProvider";
import Loader from "../../Loader/Index";
import PrimaryButton from "../CommonButtons/PrimaryButton";
import SvgComponent from "../SVGComponent";
import TickerAnimation from "../TickerAnimation";
import "./index.css";

const useServiceAuthorization = (services) => {
  const safeServices = Array.isArray(services) ? services : [];

  const getMissingServices = (service) => {
    if (!service) return [];
    const required = Array.isArray(service) ? service : [service];
    return required.filter((s) => !safeServices.includes(s));
  };

  const getConflictServices = (conflict) => {
    if (!conflict) return [];
    return Array.isArray(conflict) ? conflict : [conflict];
  };

  const hasConflict = (conflict) => {
    const conflictServices = getConflictServices(conflict);
    return (
      conflictServices.length > 0 &&
      conflictServices.every((s) => safeServices.includes(s))
    );
  };

  return { safeServices, getMissingServices, hasConflict };
};

const WeeklySchedulesCard = ({
  header,
  approval,
  list,
  actions,
  services = [],
  formatPendingCount,
  onFeatureNotAssigned,
  isFullHeightRequired,
}) => {
  const { safeServices, getMissingServices } =
    useServiceAuthorization(services);

  const formatScheduleValue = (value) => {
    if (!value) return "-";

    if (typeof value === "string" && value.trim().startsWith("INR")) {
      return value;
    }

    if (typeof value === "string") {
      const parts = value.trim().split(" ");

      if (parts.length === 3) {
        const [day, month, year] = parts;

        const parsedDay = Number(day);
        if (!Number.isNaN(parsedDay) && month && year) {
          return `${parsedDay} ${month}, ${year}`;
        }
      }
    }

    if (typeof value === "number") {
      return `INR ${value.toLocaleString("en-IN")}`;
    }

    return value;
  };

  const approvalMissing = approval ? getMissingServices(approval.service) : [];

  const approvalCount = approval
    ? formatPendingCount(approval.count, safeServices, approval.service)
    : "00";

  const approvalClickable =
    approval &&
    approvalMissing.length === 0 &&
    approval.count > GlobalConfiguration?.lengthCheckZero;

  const resolvedActions = actions.map((btn) => {
    const missing = getMissingServices(btn.service);
    const isDisabled = missing.length > 0 || btn.isDisabled === true;

    return {
      ...btn,
      missing,
      isDisabled,
    };
  });

  const handleActionClick = (btn) => {
    const missingService = btn.missing.length > 0;

    if (missingService) {
      onFeatureNotAssigned(btn.missing);
      return;
    }

    if (btn.isDisabled) {
      if (typeof btn.onDisabledClick === "function") {
        btn.onDisabledClick();
      }
      return;
    }

    btn.onClick();
  };

  return (
    <div
      className={`w-width-full ${isFullHeightRequired ? "Qlg:h-full h-schedule-card-height" : "h-schedule-card-height"} bg-primary-card rounded-border-radius-md flex flex-col`}
    >
      <div className="px-padding-md py-padding-sm flex items-center gap-gap-sm font-semibold text-text-primary text-sm">
        {header.icon && (
          <SvgComponent
            url={header.icon}
            alt="Calendar"
            color="var(--text-primary)"
            width="var(--icon-sm)"
            height="var(--icon-sm)"
          />
        )}
        {header.title}
      </div>
      {!approval && (
        <div
          className={`border-t border-primary-bg mx-margin-md ${list.handleClick ? "mx-margin-sm " : ""}`}
        ></div>
      )}

      {approval && (
        <div
          className={`px-padding-md py-padding-sm bg-primary-nav-act flex justify-between items-center ${
            approvalClickable && !approval?.isDisabled
              ? "global-hover-white relative z-z-index-base cursor-pointer"
              : "opacity-light-opacity cursor-not-allowed"
          }`}
          onClick={() => {
            if (approvalMissing.length > 0) {
              onFeatureNotAssigned(approvalMissing);
              return;
            }
            if (approval.count === GlobalConfiguration?.lengthCheckZero) return;
            approval.onClick();
          }}
        >
          <div className="flex items-center gap-gap-sm">
            <span className="text-font-lg text-pending font-semibold">
              {approvalCount}
            </span>
            <span className="text-font-xs text-text-primary">
              {approval.label}
            </span>
          </div>

          <SvgComponent
            url={ICONS?.ARROW_RIGHT_WHITE}
            alt="Next"
            color="var(--text-primary)"
            width="var(--icon-sm)"
            height="var(--icon-sm)"
          />
        </div>
      )}

      <div
        className={`flex-1  mb-margin-sm w-width-full overflow-y-auto ${list.handleClick ? "px-padding-sm" : "pr-padding-button-l pl-padding-md"}`}
      >
        {list?.loading ? (
          <div className="h-full flex justify-center items-center">
            <Loader height="var(--icon-lg)" width="var(--icon-lg)" />
          </div>
        ) : list.data.length > GlobalConfiguration?.lengthCheckZero ? (
          list.data.map((row, index) => (
            <div
              key={index}
              className={`  border-b border-primary-bg last:border-none flex-1 ${list.handleClick ? "cursor-pointer   " : "cursor-default"}`}
              onClick={() => {
                list?.handleClick?.(list?.getID(row));
              }}
            >
              <div
                className={` py-padding-xs flex justify-between items-center ${list.handleClick ? " px-padding-sm rounded-md hover:bg-primary-act my-margin-xs" : ""}`}
              >
                <div className="flex flex-col gap-gap-xs w-width-md text-text-primary font-semibold text-font-xs">
                  <div className="flex-1 truncate flex ">
                    <TickerAnimation text={list.getTitle(row) || "-"} />
                  </div>
                  <div className="text-font-xxs text-text-secondary font-normal">
                    {list.titleLabel}
                  </div>
                </div>
                <div
                  className={`${!list?.rightIcon && "flex flex-col gap-gap-xs"} w-width-md text-right`}
                >
                  {list?.rightIcon ? (
                    <div className="inline-block">
                      <img
                        src={list.rightIcon(row)}
                        alt="Status"
                        className="h-icon-sm w-icon-sm"
                      />
                    </div>
                  ) : (
                    <>
                      <p
                        className="text-font-xs text-text-primary"
                        style={{
                          color: list?.labelColor || "var(--text-secondary)",
                        }}
                      >
                        {formatScheduleValue(list.getDate(row))}
                      </p>
                      <p className="text-font-xxs text-text-secondary">
                        {list?.dateLabel}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="h-full flex justify-center items-center text-center text-font-xs text-text-primary">
            {list.emptyText}
          </div>
        )}
      </div>

      <div className="flex gap-gap-sm px-padding-md mb-margin-sm">
        {resolvedActions.map((btn, index) => (
          <PrimaryButton
            key={index}
            btnText={btn.label}
            isDisabled={btn.isDisabled}
            handleFunction={() => handleActionClick(btn)}
            enabledBgColor={btn.enabledBgColor}
            enabledTextColor={btn.enabledTextColor}
            outLineColor={btn.outLineColor}
            hoverOption={btn.hoverOption}
            fullWidthRequired={btn.fullWidthRequired}
          />
        ))}
      </div>
    </div>
  );
};

export default WeeklySchedulesCard;
